import { useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";

const FORM_ACTION = "https://readdy.ai/api/form/db7jjd73hiaolq192i40";

export default function Contact() {
  useSeo({
    title: "联系我们 - 出轨的妻子影视大全",
    description:
      "联系「出轨的妻子」影视资料站，欢迎通过反馈表单或联系邮箱提交内容建议、纠错与交流意见，我们会认真查看每一条留言。",
    keywords: "联系我们,反馈表单,联系邮箱,影视交流,内容纠错",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [content, setContent] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        params.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const lower = String(serverMsg).toLowerCase();
      const codeOk = parsed ? parsed.code === "OK" : true;

      if (response.ok && codeOk && !lower.includes("spam")) {
        setStatus("success");
        setContent("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          lower.includes("spam")
            ? "提交内容被判定为异常，请检查后重试。"
            : serverMsg || "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

      <header className="mt-6 mb-10">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">联系我们</h1>
        <p className="mt-3 text-sm md:text-base leading-7 text-foreground-600 max-w-3xl">
          对网站内容有任何建议、纠错或合作想法？欢迎通过下方表单留言，或直接发送邮件给我们，我们会认真查看每一条反馈。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contact info */}
        <aside className="lg:col-span-1 space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-3">
              <i className="ri-mail-line text-lg" />
            </span>
            <h2 className="font-heading text-base font-semibold text-foreground-950">联系邮箱</h2>
            <p className="mt-2 text-sm text-foreground-600">欢迎来信交流与反馈</p>
            <a
              href="mailto:contact@chuguideqizi.com"
              className="mt-2 inline-block text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              contact@chuguideqizi.com
            </a>
          </div>

          <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-200 text-secondary-900 mb-3">
              <i className="ri-time-line text-lg" />
            </span>
            <h2 className="font-heading text-base font-semibold text-foreground-950">回复说明</h2>
            <p className="mt-2 text-sm leading-6 text-foreground-600">
              我们通常会在 2 至 3 个工作日内查看来信。若涉及内容纠错，请尽量附上具体页面与说明，便于我们核对。
            </p>
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-1">反馈表单</h2>
            <p className="text-sm text-foreground-500 mb-6">带 <span className="text-accent-500">*</span> 的为必填项</p>

            {status === "success" ? (
              <div className="flex flex-col items-center text-center py-10">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-4">
                  <i className="ri-checkbox-circle-line text-2xl" />
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">提交成功，感谢你的反馈！</h3>
                <p className="mt-2 text-sm text-foreground-600 max-w-sm leading-6">
                  我们已经收到你的留言，会尽快查看。你可以继续浏览站内其他影视与影评内容。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-5 inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line" />
                  再写一条
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} action={FORM_ACTION} method="post" data-readdy-form className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="name@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="contact-content" className="block text-sm font-medium text-foreground-800">
                      反馈内容 <span className="text-accent-500">*</span>
                    </label>
                    <span className="text-xs text-foreground-400">{content.length} / 500</span>
                  </div>
                  <textarea
                    id="contact-content"
                    name="content"
                    required
                    maxLength={500}
                    rows={6}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="请描述你的建议、纠错或想法（最多 500 字）"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                  />
                </div>

                {/* anti-spam honeypot */}
                <div className="form-notes-hidden-field">
                  <label htmlFor="website_alt">请勿填写此项</label>
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === "error" && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-50 px-3.5 py-2.5 text-sm text-accent-700">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}